import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';

import { ApiError, apiClient, bootstrapPath } from '@/lib/api-client';
import type {
  BootstrapPoolResponse,
  BootstrapPoolWriteResponse,
  BootstrapSeed,
} from '@/types/bootstrap';

/** Centralized query keys for the bootstrap-pool editor. */
export const bootstrapKeys = {
  all: ['bootstrap-pool'] as const,
  pool: () => ['bootstrap-pool', 'pool'] as const,
};

/** GET /admin/bootstrap-pool — the current Reality edge pool from Cloudflare KV. */
export function useBootstrapPool(): UseQueryResult<BootstrapPoolResponse, ApiError> {
  return useQuery({
    queryKey: bootstrapKeys.pool(),
    queryFn: ({ signal }) => apiClient.get<BootstrapPoolResponse>(bootstrapPath(), signal),
    // KV rarely changes out from under the operator; don't refetch aggressively
    // and risk clobbering an in-progress edit on window refocus.
    staleTime: 30_000,
    refetchOnWindowFocus: false,
  });
}

/**
 * PUT /admin/bootstrap-pool — replace the whole pool. Invalidates the pool query
 * on success so the editor reflects exactly what KV now holds. A 503 (KV not
 * configured) / 502 (upstream CF error) / 400 (validation) surfaces as ApiError.
 */
export function useUpdateBootstrapPool(): UseMutationResult<
  BootstrapPoolWriteResponse,
  ApiError,
  BootstrapSeed[]
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (edges) =>
      apiClient.put<BootstrapPoolWriteResponse>(bootstrapPath(), { edges }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: bootstrapKeys.all });
    },
  });
}
